import { ArrowLeft, Eye, EyeOff, Mail, Lock, CheckCircle, Smartphone } from 'lucide-react';
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { AnimatedDots } from '../../components/ui/AnimatedDots';
import { supabase } from '../../lib/supabase';
import { getAuthErrorMessage } from '../../utils/authErrorMessages';
import { detectPlatform } from '../../utils/platformDetection';
import { buildScheme } from '../DeepLinkUtils';
import { MOBILE_APP_PAUSED } from '../../constants/appStore';
import { validatePassword } from '@rollops/app-shared/utils/passwordPolicy';
import { PasswordRulesList } from '../../components/auth/PasswordRulesList';

// Toast hook
function useToast(duration = 3000) {
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = React.useCallback((message: string, type: 'success' | 'error') => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setToast({ message, type });
    timeoutRef.current = setTimeout(() => setToast(null), duration);
  }, [duration]);

  return { toast, showToast };
}

type ResetStep = 'request' | 'processing' | 'set-password' | 'success' | 'mobile-choice';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState<ResetStep>('processing');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasTokens, setHasTokens] = useState(false);
  const [mobileDeepLink, setMobileDeepLink] = useState<string | null>(null);
  const { toast, showToast } = useToast();

  // Parse tokens from URL on mount
  useEffect(() => {
    const processTokens = async () => {
      // Check both hash and search params for tokens
      const hash = window.location.hash?.startsWith('#') ? window.location.hash.slice(1) : '';
      const hashParams = new URLSearchParams(hash);
      const searchParams = new URLSearchParams(window.location.search);

      const accessToken = hashParams.get('access_token') || searchParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token') || searchParams.get('refresh_token');
      const type = hashParams.get('type') || searchParams.get('type');
      const code = hashParams.get('code') || searchParams.get('code');
      const errorCode = hashParams.get('error_code') || searchParams.get('error_code');
      const errorDescription = hashParams.get('error_description') || searchParams.get('error_description');

      // Supabase returned an error in the URL (e.g. expired/invalid link)
      if (errorCode || errorDescription) {
        showToast(
          errorDescription?.replace(/\+/g, ' ') || 'Invalid or expired reset link. Please request a new one.',
          'error'
        );
        setStep('request');
        return;
      }

      // PKCE flow: Supabase returns a `code` that must be exchanged for a session
      if (code && !accessToken) {
        setHasTokens(true);

        // Build deep link for mobile app (pass code through so native app can exchange it)
        const mobileParams = new URLSearchParams();
        mobileParams.set('code', code);
        if (type) mobileParams.set('type', type);
        setMobileDeepLink(buildScheme(`/reset-password?${mobileParams.toString()}`));

        const currentPlatform = detectPlatform();
        const isOnMobile = currentPlatform === 'ios' || currentPlatform === 'android';

        // While MOBILE_APP_PAUSED, skip the "open in app vs. continue on web"
        // choice screen — the app is delisted, so web is the only path.
        if (isOnMobile && !MOBILE_APP_PAUSED) {
          setStep('mobile-choice');
          return;
        }

        try {
          const { error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) {
            showToast(getAuthErrorMessage(error) || 'Invalid or expired reset link. Please request a new one.', 'error');
            setStep('request');
            return;
          }
          setStep('set-password');
        } catch (err) {
          showToast('Something went wrong. Please try again.', 'error');
          setStep('request');
        }
        return;
      }

      // Legacy implicit flow: access_token + refresh_token pair
      if (accessToken && refreshToken) {
        setHasTokens(true);

        // Build deep link for mobile app
        const params = new URLSearchParams();
        params.set('access_token', accessToken);
        params.set('refresh_token', refreshToken);
        if (type) params.set('type', type);
        const deepLink = buildScheme(`/reset-password?${params.toString()}`);
        setMobileDeepLink(deepLink);

        // Check if user is on mobile - give them choice to continue in app
        const currentPlatform = detectPlatform();
        const isOnMobile = currentPlatform === 'ios' || currentPlatform === 'android';

        // While MOBILE_APP_PAUSED, skip the "open in app vs. continue on web"
        // choice screen — the app is delisted, so web is the only path.
        if (isOnMobile && !MOBILE_APP_PAUSED) {
          // On mobile: Give user choice to open app or continue on web
          setStep('mobile-choice');
          return;
        }

        // Desktop: Set session and show password form
        try {
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });

          if (error) {
            showToast(getAuthErrorMessage(error) || 'Invalid or expired reset link. Please request a new one.', 'error');
            setStep('request');
            return;
          }

          setStep('set-password');
        } catch (err) {
          showToast('Something went wrong. Please try again.', 'error');
          setStep('request');
        }
      } else {
        // No tokens - show request form
        setStep('request');
      }
    };

    processTokens();
  }, [showToast]);

  // Handle requesting a password reset email
  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      showToast('Please enter your email address', 'error');
      return;
    }

    setLoading(true);

    try {
      // The redirect URL should point back to this web page
      const webBaseUrl = window.location.origin;
      const redirectTo = `${webBaseUrl}/reset-password`;

      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo,
      });

      if (error) {
        showToast(getAuthErrorMessage(error) || 'Failed to send reset email. Please try again.', 'error');
      } else {
        showToast('Check your email for a password reset link!', 'success');
        // Clear the email field and show a confirmation message
        setEmail('');
      }
    } catch (err) {
      showToast('Something went wrong. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Handle setting the new password
  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!password.trim()) {
      showToast('Please enter a new password', 'error');
      return;
    }

    const passwordCheck = validatePassword(password);
    if (!passwordCheck.ok) {
      showToast(passwordCheck.reason, 'error');
      return;
    }

    if (password !== confirmPassword) {
      showToast('Passwords do not match', 'error');
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: password.trim(),
      });

      if (error) {
        showToast(getAuthErrorMessage(error) || 'Failed to update password. Please try again.', 'error');
      } else {
        setStep('success');
      }
    } catch (err) {
      showToast('Something went wrong. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Render based on current step
  const renderContent = () => {
    switch (step) {
      case 'processing':
        return (
          <div className="flex flex-col items-center py-8">
            <div className="w-8 h-8 border-2 border-[#9B1421] border-t-transparent rounded-full animate-spin mb-4" />
            <p className="text-[#A9A3A3]">
              <AnimatedDots text="Processing" />
            </p>
          </div>
        );

      case 'mobile-choice':
        return (
          <div className="space-y-6">
            <p className="text-[#A9A3A3] text-center">
              You can reset your password in the RollOps app or continue here on the web.
            </p>

            <div className="space-y-4">
              <Button
                onClick={() => {
                  if (mobileDeepLink) {
                    window.location.href = mobileDeepLink;
                  }
                }}
                variant="red"
                size="lg"
                className="w-full font-semibold flex items-center justify-center gap-2"
              >
                <Smartphone className="h-5 w-5" />
                Open in RollOps App
              </Button>

              <Button
                onClick={async () => {
                  // User chose to continue on web - set session and show form
                  setLoading(true);
                  const hash = window.location.hash?.startsWith('#') ? window.location.hash.slice(1) : '';
                  const hashParams = new URLSearchParams(hash);
                  const searchParams = new URLSearchParams(window.location.search);
                  const accessToken = hashParams.get('access_token') || searchParams.get('access_token');
                  const refreshToken = hashParams.get('refresh_token') || searchParams.get('refresh_token');
                  const code = hashParams.get('code') || searchParams.get('code');

                  try {
                    if (code && !accessToken) {
                      const { error } = await supabase.auth.exchangeCodeForSession(code);
                      if (error) {
                        showToast('Invalid or expired reset link. Please request a new one.', 'error');
                        setStep('request');
                      } else {
                        setStep('set-password');
                      }
                    } else if (accessToken && refreshToken) {
                      const { error } = await supabase.auth.setSession({
                        access_token: accessToken,
                        refresh_token: refreshToken,
                      });
                      if (error) {
                        showToast('Invalid or expired reset link. Please request a new one.', 'error');
                        setStep('request');
                      } else {
                        setStep('set-password');
                      }
                    } else {
                      setStep('request');
                    }
                  } catch {
                    showToast('Something went wrong. Please try again.', 'error');
                    setStep('request');
                  }
                  setLoading(false);
                }}
                disabled={loading}
                variant="outline"
                size="lg"
                className="w-full font-semibold border-[#504A4A] text-[#EBFFEE] hover:bg-[#363232] hover:text-[#EBFFEE] bg-transparent"
              >
                {loading ? <AnimatedDots text="Loading" /> : 'Continue on Web'}
              </Button>
            </div>

            <p className="text-xs text-[#504A4A] text-center">
              If you don&apos;t have the app installed, choose &quot;Continue on Web&quot;.
            </p>
          </div>
        );

      case 'request':
        return (
          <form onSubmit={handleRequestReset} className="space-y-6">
            <p className="text-[#A9A3A3] text-center">
              Enter your email address and we&apos;ll send you a link to reset your password.
            </p>

            {/* Email Input */}
            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium text-[#A9A3A3]">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#504A4A]" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  maxLength={254}
                  autoComplete="email"
                  className="w-full pl-10 pr-4 py-3 rounded-lg bg-[#111010] border border-[#504A4A] text-[#EBFFEE] placeholder:text-[#504A4A] focus:outline-none focus:border-[#9B1421] focus:ring-1 focus:ring-[#9B1421] transition-colors"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              variant="red"
              size="lg"
              className="w-full font-semibold"
            >
              {loading ? <AnimatedDots text="Sending" /> : 'Send Reset Link'}
            </Button>

            <div className="text-center">
              <Link
                to="/signin"
                className="text-sm text-[#A9A3A3] hover:text-[#EBFFEE] transition-colors"
              >
                Back to Sign In
              </Link>
            </div>
          </form>
        );

      case 'set-password':
        return (
          <form onSubmit={handleSetPassword} className="space-y-6">
            <p className="text-[#A9A3A3] text-center">
              Enter your new password below.
            </p>

            {/* New Password Input */}
            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium text-[#A9A3A3]">
                New Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#504A4A]" />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="new-password"
                  aria-describedby="reset-password-rules"
                  className="w-full pl-10 pr-12 py-3 rounded-lg bg-[#111010] border border-[#504A4A] text-[#EBFFEE] placeholder:text-[#504A4A] focus:outline-none focus:border-[#9B1421] focus:ring-1 focus:ring-[#9B1421] transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#504A4A] hover:text-[#A9A3A3] transition-colors"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              <PasswordRulesList id="reset-password-rules" password={password} />
            </div>

            {/* Confirm Password Input */}
            <div className="space-y-2">
              <label htmlFor="confirmPassword" className="text-sm font-medium text-[#A9A3A3]">
                Confirm Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#504A4A]" />
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="new-password"
                  className="w-full pl-10 pr-12 py-3 rounded-lg bg-[#111010] border border-[#504A4A] text-[#EBFFEE] placeholder:text-[#504A4A] focus:outline-none focus:border-[#9B1421] focus:ring-1 focus:ring-[#9B1421] transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#504A4A] hover:text-[#A9A3A3] transition-colors"
                >
                  {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              variant="red"
              size="lg"
              className="w-full font-semibold"
            >
              {loading ? <AnimatedDots text="Updating" /> : 'Set New Password'}
            </Button>
          </form>
        );

      case 'success':
        return (
          <div className="space-y-6 text-center">
            <div className="flex justify-center">
              <CheckCircle className="h-16 w-16 text-[#22C55E]" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-[#EBFFEE] mb-2">
                Password Updated!
              </h3>
              <p className="text-[#A9A3A3]">
                Your password has been successfully changed. You can now sign in with your new password.
              </p>
            </div>
            <Button
              onClick={() => navigate('/signin')}
              variant="red"
              size="lg"
              className="w-full font-semibold"
            >
              Go to Sign In
            </Button>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-[#111010] flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 font-['Inter'] relative">
      {/* Back Button */}
      <Link
        to="/"
        className="absolute top-4 left-4 sm:top-6 sm:left-6 flex items-center gap-2 text-[#A9A3A3] hover:text-[#EBFFEE] transition-colors group"
      >
        <ArrowLeft className="h-5 w-5 group-hover:-translate-x-1 transition-transform" />
        <span className="text-sm font-medium">Back</span>
      </Link>

      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-6 left-1/2 -translate-x-1/2 z-50 px-6 py-3 rounded-lg shadow-lg transition-all duration-300 ${
            toast.type === 'success' 
              ? 'bg-[#22C55E] text-white' 
              : 'bg-[#9B1421] text-white'
          }`}
        >
          {toast.message}
        </div>
      )}

      <div className="w-full max-w-md flex flex-col items-center">
        {/* Logo and Brand */}
        <div className="flex items-center space-x-4 mb-12">
          <img 
            src="/rollops-logo.png" 
            alt="RollOps Logo" 
            className="w-20 h-20 sm:w-24 sm:h-24"
          />
          <span className="text-4xl sm:text-5xl font-bold text-[#EBFFEE]">
            Roll<span className="text-rollops-accent">O</span>ps
          </span>
        </div>

        {/* Main Card */}
        <Card className="w-full border-[#504A4A] bg-[#221F1F] shadow-lg">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-2xl font-bold text-center text-[#EBFFEE]">
              {step === 'success' ? 'Success!' : 
               step === 'mobile-choice' ? 'Reset Password' :
               hasTokens ? 'Set New Password' : 'Reset Password'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {renderContent()}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
